/**
 * One look for every "this is what to do next" hint.
 *
 * These pulses guide a first-time user through a sequence — analyse a run, pick
 * a race, predict, send it to the watch — so they have to read as the same
 * signal wherever they appear. Two of them in different colours read as two
 * unrelated warnings, which is why the palette and the timing live here rather
 * than at each call site.
 *
 * Orange, because the pulse has to be a colour the UI never uses for a control.
 * Buttons are already blueAccent and state is greenAccent, so a pulse in either
 * of those barely registers as anything — it looks like the button's own hover.
 * colors.warnings.orange is reserved for emphasis, is identical in light and
 * dark mode, and appears on nothing clickable.
 *
 * Opacities rather than solid fills: the pulse sits behind icons and label text
 * that keep their own colours, and a solid orange would bury them.
 *
 * The animations themselves stay at their call sites, because they animate
 * different properties — a grid column tints its background, an outlined button
 * brightens its border and glows. Only the colours and the timing are shared.
 */
export const attentionPulse = (colors) => ({
  /** Border / icon colour at the peak of the pulse. */
  edge: colors.warnings.orange,
  /** Surface tint at the peak, for blocks and grid cells. */
  tint: `${colors.warnings.orange}40`,
  /** Translucent ring around an outlined control. */
  glow: `${colors.warnings.orange}66`,
  /** Faint fill for an outlined control, which must stay readable. */
  fill: `${colors.warnings.orange}26`,
});

/** One cycle. Four of them is the 6s the pulse runs for. */
export const PULSE_CYCLE = '1.5s';
export const PULSE_ITERATIONS = 4;
/** Slightly past 4 × 1.5s, so state clears just after the animation lands. */
export const PULSE_TOTAL_MS = 6300;
/** Ready-made shorthand: `animation: pulseAnimation('myKeyframes')`. */
export const pulseAnimation = (keyframesName) =>
  `${keyframesName} ${PULSE_CYCLE} ease-in-out ${PULSE_ITERATIONS}`;
